import React, { Component } from "react"
import PropTypes from "prop-types"
import {
  withRadium,
  Price,
  Specs,
  DetectCompact,
  ConnectLocation,
  gtmEvent,
  SearchParams
} from "higher-order-components"
import {
  InlineDisclaimerBlock,
  ConfiguredVehicle,
} from "containers"
import { Section, ColumnGroup, Space, A, FormGroup, H3, H6, Caption, InputCheckBox, InfoIcon, H5, Modal, Button, Content, Interactive, LoadingIcon, RestartIcon } from 'AtomicComponents'
import { fromLinkInputs, formSetDefaultValues } from "AtomicUtils/formHandler"
import { Select, TabsResponsive } from "components"
import { bindAll, take, get, noop, parseKoalaData, rafThrottle } from "utils"
import exposeGlobal from "apis/expose-global"
// Redux
import { compareToolActionTypes } from "store/gtm-events"
import { Disclaimer, SaveShareCopy, AccordionText, Tabs } from "AtomicContainers"
import componentParser from "AtomicUtils/componentParser"
import { WINDOW } from "AtomicUtils/environment"
import debounce from 'AtomicUtils/debounce'

const Arrows = () => (
  <svg viewBox="0 0 29 29" fill="#aaa" width="2em">
    <path d="M1.25,9H24.5l-4,3.92a1.22,1.22,0,1,0,1.74,1.71l6.44-6.14a1.21,1.21,0,0,0,0-1.71L22.17,0.35a1.21,1.21,0,0,0-1.71,1.71L24.5,6H1.25A1.53,1.53,0,0,0,1.25,9Z" />
    <path d="M29,21.5A1.52,1.52,0,0,0,27.75,20H4.5l4-3.94a1.21,1.21,0,1,0-1.71-1.71L0.35,20.78a1.21,1.21,0,0,0,0,1.71l6.44,6.14a1.22,1.22,0,0,0,1.74-1.71L4.5,23H27.75A1.52,1.52,0,0,0,29,21.5Z" />
  </svg>
)

const X = () => (
  <svg data-test="cross" viewBox="0 0 18 18" height="1em">
    <path
      fill="#910A2D"
      d="M10.74,9l6.9-6.9A1.23,1.23,0,1,0,15.9.36L9,7.26,2.1,0.36A1.23,1.23,0,0,0,.36,2.1L7.26,9l-6.9,6.9A1.23,1.23,0,1,0,2.1,17.64L9,10.74l6.9,6.9a1.23,1.23,0,1,0,1.74-1.74Z"
    />
  </svg>
)

const Check = () => (
  <svg data-test="check" viewBox="0 0 22 18" height="1em">
    <path
      fill="#4C607E"
      d="M8.46,18a1.22,1.22,0,0,1-.87-0.37L0.36,10.3a1.26,1.26,0,0,1,0-1.77,1.22,1.22,0,0,1,1.74,0l6.26,6.35L19.81,0.46A1.22,1.22,0,0,1,21.54.28,1.26,1.26,0,0,1,21.73,2L9.41,17.54a1.23,1.23,0,0,1-.89.46H8.46Z"
    />
  </svg>
)

const RenderPrice = ({
  selected,
  price,
  sticky,
  compact,
  displayPrice,
  safetyModel,
  dataTest,
  onClickPriceDisclaimer
}) => (
  <div data-test={dataTest} style={priceStyles.pricingDiv(selected, compact)}>
    {displayPrice &&
      price &&
      price.price && (
        <span style={priceStyles.priceWrapper(compact)}>
          <span style={priceStyles.rdp(compact)}>RRP FROM</span>
          <InlineDisclaimerBlock
            message={price.disclaimer}
            gtmEvents={[{ func: onClickPriceDisclaimer }]}
            blockStyling={priceStyles.actualPrice(sticky)}
            disclaimerStyling={{
              marginLeft: ".1em",
              color: "inherit",
              fontSize: "inherit"
            }}>
            ${safetyModel && price.safetyModelPrice ? price.safetyModelPrice.toLocaleString('nz') : price.price.toLocaleString('nz')}
          </InlineDisclaimerBlock>
        </span>
      )}
    {displayPrice &&
      price &&
      !price.price &&
      price.message && (
        <span style={priceStyles.pricingSpan}>{price.message}</span>
      )}
    {!displayPrice && (
      <span style={priceStyles.pricingSpan}>Choose a grade to see price</span>
    )}
  </div>
)

RenderPrice.propTypes = {
  selected: PropTypes.bool,
  dataTest: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  sticky: PropTypes.bool,
  displayPrice: PropTypes.bool,
  compact: PropTypes.bool,
  onClickPriceDisclaimer: PropTypes.func
}

const priceStyles = {
  pricingDiv: (selected, compact) => ({
    flex: "1 1 33%",
    padding: "1em",
    display: "flex",
    justifyContent: "center",
    opacity: selected ? 1 : compact ? 0.5 : 1
  }),
  priceWrapper: compact => ({
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    flexDirection: compact ? "column" : "row",
    justifyContent: "center"
  }),
  rdp: compact => ({
    fontSize: "80%",
    maxWidth: compact ? "initial" : "50px",
    textAlign: "right",
    lineHeight: 1.25,
    display: "block",
    marginRight: ".5em",
    fontWeight: 600
  }),
  actualPrice: sticky => ({
    fontSize: sticky ? "220%" : "180%",
    transition: ".3s",
    whiteSpace: "nowrap",
    fontWeight: 400
  }),
  pricingSpan: {
    maxWidth: "180px",
    lineHeight: 1.5,
    margin: "auto",
    display: "block",
    textTransform: "uppercase",
    fontSize: "80%",
    fontWeight: "600",
    textAlign: "center",
    letterSpacing: ".2em",
    opacity: 0.8
  }
}

// Lines that compare specs inside the accordion
const DuoLine = props => (
  <div style={dstyles.base}>
    {props.compact && <div style={dstyles.div}>{props.title}</div>}
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
      <span style={dstyles.span}>
        {props.showLeft ? (
          <A
            withoutStyle
            style={{ fontSize: "22px" }}
            onClick={(event) => {
              if (event && event.target && event.target.querySelector('button')){
                event.target.querySelector('button').click()
              }
            }}>
            {props.contentA ? (
              props.contentA === "Standard" ? (
                <Check />
              ) : (
                props.contentA
              )
            ) : (
              <X />
            )}
            {
              props.disclaimerA &&
              <Disclaimer text={props.disclaimerA && props.disclaimerA.disclaimerCode}>
                {props.disclaimerA && props.disclaimerA.disclaimerText}
              </Disclaimer>
            }
          </A>
        ) : (
          ""
        )}
      </span>
      {!props.compact && <div style={dstyles.div}>{props.title}</div>}
      <span style={dstyles.span}>
        {props.showRight ? (
          <A
            withoutStyle
            style={{ fontSize: "22px" }}
            onClick={(event) => {
              if (event && event.target && event.target.querySelector('button')){
                event.target.querySelector('button').click()
              }
            }}>
            {props.contentB ? (
              props.contentB === "Standard" ? (
                <Check />
              ) : (
                props.contentB
              )
            ) : (
              <X />
            )}
            {
              props.disclaimerB &&
              <Disclaimer text={props.disclaimerB && props.disclaimerB.disclaimerCode}>
                {props.disclaimerB && props.disclaimerB.disclaimerText}
              </Disclaimer>
            }
          </A>
        ) : (
          ""
        )}
      </span>
    </div>
  </div>
)

DuoLine.propTypes = {
  showLeft: PropTypes.bool,
  showRight: PropTypes.bool,
  title: PropTypes.string,
  contentA: PropTypes.string,
  contentB: PropTypes.string,
  disclaimerA: PropTypes.object,
  disclaimerB: PropTypes.object,
  compact: PropTypes.bool
}

const dstyles = {
  base: {
    padding: "2em 0",
    borderBottom: "2px solid #eee"
  },
  div: {
    fontWeight: 800,
    color: "#aaaaaa",
    letterSpacing: ".2em",
    textAlign: "center",
    lineHeight: 2,
    textTransform: "uppercase",
    fontSize: "80%",
    flex: "0 0 33%"
  },
  span: {
    flex: "1 1 33%",
    textAlign: "center",
    paddingTop: ".2em"
  }
}

const RenderSelection = ({ heading, options, onChange }) => (
  <div data-test="compare-tool-selection" style={{ width: "100%" }}>
    {heading && (
      <H5 withDetails>{options.nameplate.name}</H5>
    )}
    <div style={{ width: "100%", margin: "auto", position: "relative", overflow: "hidden" }}>
      <TabsResponsive
        options={options.bodyStyles}
        onChange={onChange}
        active={options.bodyStyles.indexOf(options.bodyStyle)}
        label="Body"
        divider
      />
      <FormGroup>
        <Select
          key={`SelectBody-${options.bodyStyles.indexOf(options.bodyStyle)}`}
          options={options.grades}
          onChange={onChange}
          active={options.grades.indexOf(options.grade)}
          label="Grade"
        />
      </FormGroup>
      <FormGroup>
        <Select
          key={`SelectBodyGrade-${options.bodyStyles.indexOf(options.bodyStyle)}-${options.grades.indexOf(options.grade)}`}
          options={options.engines.filter(engine => !engine.isSafetyModel)}
          onChange={onChange}
          active={options.engines.indexOf(options.engine)}
          disabled={!options.grade}
          label="Engine"
        />
      </FormGroup>
    </div>
  </div>
)

const CTAPair = ({
  gtmID,
  isVisible,
  findDealerUrl,
  compact,
  vehicle,
  onClickCTA
}) => (
  <div
    style={{
      flex: compact ? "1 1 100%" : `1 1 auto`,
      visibility: isVisible ? "visible" : "hidden",
      alignItems: "center",
      flexDirection: compact ? "column" : "row",
      boxSizing: "border-box",
      padding: "0 1em"
    }}
    data-test={`${vehicle.nameplate.name}-ctas`}>
    <ColumnGroup>
      {findDealerUrl && (
        <Button
          type="a"
          label="Find a dealer"
          href={findDealerUrl}
          styleName="red"
          fullWidth={compact}
          onClick={() => {
            onClickCTA(gtmID[0], "FIND A DEALER", vehicle)
          }}
        />
      )}
      {compact?<Space />:undefined}
      {vehicle.nameplate.vehicleLandingPageUrl && (
        <Button
          type="a"
          label="Learn more"
          href={vehicle.nameplate.vehicleLandingPageUrl}
          styleName="blackOutlined"
          fullWidth={compact}
          onClick={() => {
            onClickCTA(gtmID[1], "LEARN MORE", vehicle)
          }}
        />
      )}
    </ColumnGroup>
  </div>
)
CTAPair.propTypes = {
  gtmID: PropTypes.array,
  isVisible: PropTypes.bool,
  findDealerUrl: PropTypes.string,
  compact: PropTypes.bool,
  vehicle: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onClickCTA: PropTypes.func
}

const VehicleCTAs = ({
  isLeftCTAsVisible,
  isRightCTAsVisible,
  vehicleLeft,
  vehicleRight,
  findDealerUrl,
  compact,
  activeOptions,
  onClickCTA
}) => (
  <div
    style={{
      width: "100%",
      maxWidth: "1200px",
      margin: "0 auto",
      display: "flex",
      justifyContent: "center",
      padding: "1em 0 4em",
      fontSize: "0.96em",
      textAlign: "center"
    }}>
    <div style={{ flex: (!compact || activeOptions === 0) ? "1 0 33%" : "inherit" }}>
      {(!compact || activeOptions === 0) && (
        <CTAPair
          gtmID={["FED010", "FED011"]}
          isVisible={isLeftCTAsVisible}
          findDealerUrl={findDealerUrl}
          compact={compact}
          vehicle={vehicleLeft}
          onClickCTA={onClickCTA}
        />
      )}
    </div>
    <div style={{ flex: !compact ? "0 0 33%" : "inherit" }} />
    <div style={{ flex: (!compact || activeOptions === 1) ? "1 0 33%" : "inherit" }}>
      {(!compact || activeOptions === 1) && (
        <CTAPair
          gtmID={["FED012", "FED013"]}
          isVisible={isRightCTAsVisible}
          findDealerUrl={findDealerUrl}
          compact={compact}
          vehicle={vehicleRight}
          onClickCTA={onClickCTA}
        />
      )}
    </div>
  </div>
)

VehicleCTAs.propTypes = {
  isLeftCTAsVisible: PropTypes.bool,
  isRightCTAsVisible: PropTypes.bool,
  vehicleLeft: PropTypes.object,
  vehicleRight: PropTypes.object,
  findDealerUrl: PropTypes.string,
  compact: PropTypes.bool,
  activeOptions: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
  onClickCTA: PropTypes.func
}

const getnameIfDefault = option => option && get("name", option)

const stateToParamsMapping = (optionsLeft, optionsRight, safetyModelLeft = false, safetyModelRight = false) => ({
  bodyStyleLeft: optionsLeft && getnameIfDefault(optionsLeft.bodyStyle),
  gradeLeft: optionsLeft && getnameIfDefault(optionsLeft.grade),
  engineLeft: optionsLeft && getnameIfDefault(optionsLeft.engine),
  modelCodeLeft: safetyModelLeft ? optionsLeft && optionsLeft.engine && get("safetyModelCode", optionsLeft.engine) : optionsLeft && optionsLeft.engine && get("modelCode", optionsLeft.engine),
  bodyStyleRight: optionsRight && getnameIfDefault(optionsRight.bodyStyle),
  gradeRight: optionsRight && getnameIfDefault(optionsRight.grade),
  engineRight: optionsRight && getnameIfDefault(optionsRight.engine),
  modelCodeRight: safetyModelRight ? optionsRight && optionsRight.engine && get("safetyModelCode", optionsRight.engine) : optionsRight && optionsRight.engine && get("modelCode", optionsRight.engine),
})

class TopNav extends Component {
  render() {
    const {
      style,
      backLink,
      hideLinks,
      locationState,
      savedCompareEndPoint,
      sharedCompareEndPoint,
      findDealerUrl,
      optionsLeft,
      optionsRight,
      form,
      safetyModelExcess,
      financeOptionText,
      bubbleDirection
    } = this.props

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "nowrap",
          ...style,
          borderBottom: "1px solid #D7D7D7",
          justifyContent: "space-between"
        }}>
        <A withoutStyle style={shareStyles.trigger} href={backLink}>
          <RestartIcon style={{ height: "22px", width: "22px", marginRight: "8px" }} />
          <Interactive>
            Restart
          </Interactive>
        </A>
        {!hideLinks && (
          <SaveShareCopy
            locationState={locationState}
            bubbleDirection={bubbleDirection}
            disableSavePrompt={this.props.disableSavePrompt}
            delayToShowSavePrompt={this.props.delayToShowSavePrompt}
            descriptionSave={"Save time and conveniently save your Mazda comparison for future reference."}
            descriptionShare={"Send a link of your Mazda compare via email"}
            imageSrc={[
              optionsLeft.grade.imageSrc,
              optionsRight.grade.imageSrc
            ]}
            findDealerUrl={findDealerUrl}
            saveEndPoint={savedCompareEndPoint}
            shareEndPoint={sharedCompareEndPoint}
            enabled={{...optionsLeft.engine, ...optionsRight.engine}}
            namePlate={[
              getnameIfDefault(optionsLeft.nameplate),
              getnameIfDefault(optionsRight.nameplate)
            ]}
            bodyStyle={[
              getnameIfDefault(optionsLeft.bodyStyle),
              getnameIfDefault(optionsRight.bodyStyle)
            ]}
            safetyModelExcess={[
              form.safetyModelLeft.value && get("safetyModelCode", optionsLeft.engine) ? (get("modelCode", optionsLeft.engine) === get("safetyModelCode", optionsLeft.engine) ? "Included as standard" : `+$${safetyModelExcess}`) : null,
              form.safetyModelRight.value && get("safetyModelCode", optionsRight.engine) ? (get("modelCode", optionsRight.engine) === get("safetyModelCode", optionsRight.engine) ? "Included as standard" : `+$${safetyModelExcess}`) : null
            ]}
            financeOptionText={financeOptionText}
            gtmEvents={{
              category: "Compare tool",
              action: `Compare "${getnameIfDefault(
                optionsLeft.nameplate
              )}", "${getnameIfDefault(
                optionsLeft.bodyStyle
              )}", "${getnameIfDefault(
                optionsLeft.grade
              )}", "${getnameIfDefault(
                optionsLeft.engine
              )}" and "${getnameIfDefault(
                optionsRight.nameplate
              )}", "${getnameIfDefault(
                optionsRight.bodyStyle
              )}", "${getnameIfDefault(
                optionsRight.grade
              )}", "${getnameIfDefault(optionsRight.engine)}"`
            }}
            compareUrl={WINDOW && WINDOW.location.href}
          />
        )}
      </div>
    )
  }
}

TopNav.propTypes = {
  backLink: PropTypes.string,
  hideLinks: PropTypes.bool,
  style: PropTypes.object,
  locationState: PropTypes.object,
  savedCompareEndPoint: PropTypes.string,
  sharedCompareEndPoint: PropTypes.string,
  findDealerUrl: PropTypes.string,
  optionsLeft: PropTypes.object,
  optionsRight: PropTypes.object
}

const shareStyles = {
  trigger: {
    cursor: "pointer",
    fill: "#aaa",
    color: "#aaa",
    display: "flex",
    padding: "1em 1.8em 1em 1.5em",
    alignItems: "center",
    textTransform: "uppercase",
    letterSpacing: "0.2em",
    fontWeight: "600",
    textDecoration: "none"
  },
  link: {
    color: "#aaa",
    textDecoration: "none",
    fontSize: "90%",
    padding: "0em 0em 5em",
    ":hover": {
      color: "#101010"
    }
  }
}

const apiMapping = stateOptions => ({
  nameplate: stateOptions.nameplate.name,
  bodystyle: stateOptions.bodyStyle.name,
  drivetrain: stateOptions.engine.drivetrain,
  grade: stateOptions.grade.name,
  transmission: stateOptions.engine.transmission,
  engineSize: stateOptions.engine.engineSize,
  fueltype: stateOptions.engine.fuelType,
  modelCode: stateOptions.engine.modelCode
})

/* Compare Tool */
export class CompareTool extends Component {
  constructor(props) {
    super(props)

    const [optionsLeft, optionsRight] = take(2, props.vehicles).map(data => ({
      ...parseKoalaData(data, { type: "compare" }),
      grade: false,
      engine: false
    }))

    this.state = {
      optionsLeft,
      optionsRight,
      activeOptions: 0,
      compareUrl: "",
      form: {
        safetyModelLeft: {
          validations: {
          }
        },
        safetyModelRight: {
          validations: {
          }
        },
        showDifferences: {
          validations: {
          }
        }
      },
      isModalOpen: false
    }

    bindAll(
      [
        "handleChangeOptionLeft",
        "handleChangeOptionRight",
        "updateActiveOptions",
        "getPrice",
        "getSpecs",
        "handleScroll",
        "updateStickyNav",
        "onClickCTA"
      ],
      this
    )
  }

  componentDidMount() {
    const {
      specifications,
      initialResults,
      price,
      specsApiEndpoint
    } = this.props
    const { optionsLeft, optionsRight } = this.state

    price.setOptions({
      mapping: apiMapping,
      fetchURL: this.props.priceApiEndpoint
    })
    specifications.setOptions(
      {
        mapping: apiMapping,
        initial: initialResults && { categories: initialResults },
        fetchURL: specsApiEndpoint,
        featured: false
      },
      this.getSpecs
    )
    this.updateStickyNav()
    this.scrollEvent = rafThrottle(this.handleScroll)
    WINDOW.addEventListener("scroll", debounce(this.scrollEvent, 250), {
      passive: true
    })

    exposeGlobal("getComparedVehicles", () => {
      const left = this.state.optionsLeft
      const right = this.state.optionsRight
      left.price = this.props.price.data[0]
      right.price = this.props.price.data[1]
      return {
        leftVehicle: left,
        rightVehicle: right
      }
    })

    this.handleOptionChange(
      optionsLeft.grades.find(item => !!item.default),
      "optionsLeft",
      0
    )
    this.handleOptionChange(
      optionsRight.grades.find(item => !!item.default),
      "optionsRight",
      1
    )
  }

  componentWillUnmount() {
    WINDOW.removeEventListener("scroll", debounce(this.scrollEvent,250), {
      passive: true
    });
  }

  handleScroll() {
    this.updateStickyNav()
  }

  updateStickyNav() {
    if (this.priceBarEl && this.priceBarEl.getBoundingClientRect().top > 0) {
      // Checking if value should update improves performance
      if (this.state.stickyTransform === this.overflowEl.clientHeight) return
      this.setState({ stickyTransform: this.overflowEl.clientHeight })
    } else {
      if (this.state.stickyTransform === 0) return
      this.setState({ stickyTransform: 0 })
    }
  }

  handleOptionChange(newSelection, key, index) {
    if (!newSelection) return

    const { form } = this.state
    const currentOptions = this.state[key]

    if (
      get("name", newSelection) ===
      get([newSelection.type, "name"], currentOptions)
    )
      return

    const options = parseKoalaData(
      this.props.vehicles[index],
      {
        newSelection,
        stateOptions: this.state[key],
        type: "compare"
      },
      true
    )
    if (newSelection.type === "bodyStyle")
      options.grade = options.engine = false
    else if (newSelection.type === "grade") {
      this.handleOptionChange({ type: "drivetrain" }, key, index)
    }

    const onState = ["bodyStyle", "grade", "engine"].every(
      attr => !!get(attr, options)
    )
      ? () => {
          this.getPrice(options, index)
          this.getSpecs()
          this.setState({ activeOptions: index }, () => {
            formSetDefaultValues(this, {
              safetyModelLeft: this.state.optionsLeft.engine.modelCode === this.state.optionsLeft.engine.safetyModelCode,
              safetyModelRight: this.state.optionsRight.engine.modelCode === this.state.optionsRight.engine.safetyModelCode
            })
          })

          this.props.search.pushParam({
            ...stateToParamsMapping(
              this.state.optionsLeft,
              this.state.optionsRight,
              form.safetyModelLeft.value,
              form.safetyModelRight.value
            )
          })
        }
      : () => {
          this.props.search.pushParam({
            ...stateToParamsMapping(
              this.state.optionsLeft,
              this.state.optionsRight,
              form.safetyModelLeft.value,
              form.safetyModelRight.value
            )
          })
        }
    this.setState({ [key]: options }, onState)
  }

  handleChangeOptionLeft(option) {
    // Get gtmEventID based on which component being selected
    const gtmEventID = change => {
      switch (change.type) {
        case "bodyStyle":
          return "FED002"
        case "grade":
          return "FED003"
        case "engine":
          return "FED004"
        default:
          return "ERROR!"
      }
    }
    // Constructing the pageAction message
    const model1 = this.state.optionsLeft.nameplate.name
    const model2 = this.state.optionsRight.nameplate.name
    const message =
      "Compared \"" +
      model1.toUpperCase() +
      "\" and \"" +
      model2.toUpperCase() +
      "\" - Selected \"" +
      model1.toUpperCase() +
      "\" " +
      option.type +
      " \"" +
      option.name.toUpperCase() +
      "\""
    // Dispatch gtm event
    this.props.gtmEvent({
      gtmID: compareToolActionTypes.COMPARE_TOOL_CHANGE_SELECTION,
      gtmPayload: {
        eventID: gtmEventID(option),
        actionMessage: message
      }
    })
    // Continue with react logic
    this.handleOptionChange(option, "optionsLeft", 0)
  }

  handleChangeOptionRight(option) {
    const gtmEventID = change => {
      switch (change.type) {
        case "bodyStyle":
          return "FED005"
        case "grade":
          return "FED006"
        case "engine":
          return "FED007"
        default:
          return "ERROR!"
      }
    }
    const model1 = this.state.optionsLeft.nameplate.name
    const model2 = this.state.optionsRight.nameplate.name
    const message =
      "Compared \"" +
      model1.toUpperCase() +
      "\" and \"" +
      model2.toUpperCase() +
      "\" - Selected \"" +
      model2.toUpperCase() +
      "\" " +
      option.type +
      " \"" +
      option.name.toUpperCase() +
      "\""
    this.props.gtmEvent({
      gtmID: compareToolActionTypes.COMPARE_TOOL_CHANGE_SELECTION,
      gtmPayload: {
        eventID: gtmEventID(option),
        actionMessage: message
      }
    })

    this.handleOptionChange(option, "optionsRight", 1)
  }

  getPrice(options) {
    const { update } = this.props.price

    if (!options.grade) return
    update([this.state.optionsLeft || false, this.state.optionsRight || false])
  }

  getSpecs() {
    const { update } = this.props.specifications
    const { optionsLeft, optionsRight, form: { safetyModelLeft, safetyModelRight } } = this.state

    if (!optionsLeft.engine && !optionsRight.engine) return

    update([{
      ...optionsLeft,
      engine: {
        ...optionsLeft.engine,
        modelCode: safetyModelLeft.value ? optionsLeft.engine.safetyModelCode : optionsLeft.engine.modelCode
      }
    }, {
      ...optionsRight,
      engine: {
        ...optionsRight.engine,
        modelCode: safetyModelRight.value ? optionsRight.engine.safetyModelCode : optionsRight.engine.modelCode
      }
    }])
  }

  updateActiveOptions(index) {
    this.setState({ activeOptions: index })
  }

  // CTA clicking event for GTM
  onClickCTA(eventID, CTAname, vehicle) {
    const model1 = this.state.optionsLeft.nameplate.name
    const model2 = this.state.optionsRight.nameplate.name
    const actionMessage =
      "Compared \"" +
      model1.toUpperCase() +
      "\" and \"" +
      model2.toUpperCase() +
      "\" - Clicked \"" +
      CTAname.toUpperCase() +
      "\" - \"" +
      vehicle.nameplate.name.toUpperCase() +
      "/" +
      vehicle.bodyStyle.name.toUpperCase() +
      "/" +
      vehicle.grade.name.toUpperCase() +
      "/" +
      vehicle.engine.name.toUpperCase() +
      "\""
    this.props.gtmEvent({
      gtmID: compareToolActionTypes.COMPARE_TOOL_CLICK_CTA,
      gtmPayload: {
        eventID: eventID,
        actionMessage: actionMessage
      }
    })
  }

  // Price disclaimer clicking event for GTM
  onClickPriceDisclaimer(eventID, vehicle) {
    const model1 = this.state.optionsLeft.nameplate.name
    const model2 = this.state.optionsRight.nameplate.name
    const actionMessage =
      "Compared \"" +
      model1.toUpperCase() +
      "\" and \"" +
      model2.toUpperCase() +
      "\" - Clicked Pricing Disclaimer - \"" +
      vehicle.nameplate.name.toUpperCase() +
      "/" +
      vehicle.bodyStyle.name.toUpperCase() +
      "/" +
      vehicle.grade.name.toUpperCase() +
      "/" +
      vehicle.engine.name.toUpperCase() +
      "\""
    this.props.gtmEvent({
      gtmID: compareToolActionTypes.COMPARE_TOOL_CLICK_PRICE_DISCLAIMER,
      gtmPayload: {
        eventID: eventID,
        actionMessage: actionMessage
      }
    })
  }

  renderSafetyModelOption(isLeft = true) {
    const { safetyModelExcess, safetyModelDisclaimer, isInitialSafetyModelLeft, isInitialSafetyModelRight } = this.props
    const { optionsLeft, optionsRight } = this.state
    const price = isLeft ? this.props.price.data[0] : this.props.price.data[1]

    if (isLeft && (!optionsLeft.engine || (optionsLeft.engine && !optionsLeft.engine.safetyModelCode))) {
      return null
    }

    if (!isLeft && (!optionsRight.engine || (optionsRight.engine && !optionsRight.engine.safetyModelCode))) {
      return null
    }

    return (
      <div style={{ width: "100%", margin: "auto", textAlign: "left" }}>
        <FormGroup>
          <div>
            <label>Options</label>
            <div style={styles.safetyModel}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <InputCheckBox
                  key={`SafetyModel-${isLeft ? 'Left' : 'Right'}`}
                  name={isLeft ? "safetyModelLeft" : "safetyModelRight"}
                  label="Vision Technology"
                  checked={price && price.price && (price.price === price.safetyModelPrice || (isLeft ? isInitialSafetyModelLeft : isInitialSafetyModelRight))}
                  disabled={price && price.price && price.price === price.safetyModelPrice}
                  {...fromLinkInputs(this, isLeft ? "safetyModelLeft" : "safetyModelRight", () => {
                    setTimeout(() => {
                      this.getSpecs()
                      this.props.search.pushParam({
                        ...stateToParamsMapping(
                          this.state.optionsLeft,
                          this.state.optionsRight,
                          this.state.form.safetyModelLeft.value,
                          this.state.form.safetyModelRight.value
                        )
                      })
                    }, 400)
                  })}
                />
                <span
                  onClick={() => {
                    this.setState({ isModalOpen: true })
                  }}>
                  <InfoIcon style={styles.infoIcon} />
                </span>
              </div>
              <div>
                {
                  price && price.price && price.price === price.safetyModelPrice ?
                    <label>Included as standard</label> :
                    safetyModelExcess &&
                    price &&
                    safetyModelDisclaimer ?
                    <div>
                      <InlineDisclaimerBlock
                        message={safetyModelDisclaimer}
                        disclaimerStyling={{
                          fontSize: "16px"
                        }}>
                        +${safetyModelExcess.toLocaleString()}
                      </InlineDisclaimerBlock>
                    </div>
                    :
                    <LoadingIcon
                      style={{
                        width: "16px",
                        height: "16px"
                      }}
                    />
                }
              </div>
            </div>
          </div>
        </FormGroup>
      </div>
    )
  }

  render() {
    const {
      specifications,
      price,
      compact,
      hideLinks,
      findDealerUrl,
      backLink,
      savedCompareEndPoint,
      sharedCompareEndPoint,
      delayToShowSavePrompt,
      disableSavePrompt,
      safetyModelBlurb,
      safetyModelExcess,
      financeOptionText,
      locationState,
      components
    } = this.props
    const {
      optionsLeft,
      optionsRight,
      activeOptions,
      stickyTransform,
      form
    } = this.state

    return (
      <div style={styles.base} data-test="compare-tool">
        <div
          style={[
            {
              backgroundColor: "white",
              position: "fixed",
              zIndex: 5,
              width: "100%",
              top: 0
            },
            {
              transition: ".5s",
              transform: `translateY(-${stickyTransform}px)`
            }
          ]}>
          <TopNav
            optionsLeft={optionsLeft}
            optionsRight={optionsRight}
            form={form}
            safetyModelExcess={safetyModelExcess}
            bubbleDirection="bottom"
            delayToShowSavePrompt={delayToShowSavePrompt}
            disableSavePrompt={disableSavePrompt}
            hideLinks={hideLinks}
            backLink={backLink}
            locationState={locationState}
            savedCompareEndPoint={savedCompareEndPoint}
            sharedCompareEndPoint={sharedCompareEndPoint}
            financeOptionText={financeOptionText}
            style={{ display: stickyTransform ? "none" : "flex" }}
            findDealerUrl={findDealerUrl}
          />
          {/* STICKY CAR AND GRADE NAME */}

          <Section>
            <div
              ref={c => {
                this.overflowEl = c
              }}
              style={styles.options(!this.state.stickyTransform)}>
              <div
                style={{
                  flex: compact ? "0 0 35%" : "1 1 33%",
                  alignItems: "center",
                  opacity: !compact || activeOptions === 0 ? 1 : 0.5
                }}>
                <H6 withDetails>{optionsLeft.nameplate.name}</H6>
                <Caption>
                  {optionsLeft.grade.name} - {optionsLeft.engine.name}
                </Caption>
              </div>
              <div
                style={{
                  flex: compact ? "0 0 15%" : "0 0 15%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}>
                <Arrows />
              </div>
              <div
                style={{
                  flex: compact ? "0 0 35%" : "1 1 33%",
                  alignItems: "center",
                  opacity: !compact || activeOptions === 1 ? 1 : 0.5
                }}>
                <H6 withDetails>{optionsRight.nameplate.name}</H6>
                <Caption>
                  {optionsRight.grade.name} - {optionsRight.engine.name}
                </Caption>
              </div>
            </div>
          </Section>

          {/* STICKY PRICE BAR */}

          <div style={{ background: "#565c65", color: "white" }}>
            <Section>
              <div
                style={{
                  display: this.state.stickyTransform ? "none" : "flex",
                  justifyContent: compact ? "space-around" : "center",
                  width: '100%'
                }}>
                <RenderPrice
                  dataTest="price-left"
                  selected={activeOptions === 0}
                  displayPrice={!!optionsLeft.engine}
                  compact={compact}
                  safetyModel={form.safetyModelLeft.value}
                  onClickPriceDisclaimer={() =>
                    this.onClickPriceDisclaimer(
                      "FED014",
                      this.state.optionsLeft
                    )
                  }
                  price={price.data[0]}
                />
                {!compact && <div style={{ flex: "0 0 15%" }} />}
                <RenderPrice
                  dataTest="price-right"
                  selected={activeOptions === 1}
                  displayPrice={!!optionsRight.engine}
                  compact={compact}
                  safetyModel={form.safetyModelRight.value}
                  onClickPriceDisclaimer={() =>
                    this.onClickPriceDisclaimer(
                      "FED015",
                      this.state.optionsRight
                    )
                  }
                  price={price.data[1]}
                />
              </div>
            </Section>
          </div>
        </div>
        <div
          style={[
            {
              position: "relative",
              backgroundColor: compact ? "#fff" : "#f5f5f5"
            },
            { opacity: this.state.stickyTransform ? 1 : 0 }
          ]}>
          <TopNav
            optionsLeft={optionsLeft}
            optionsRight={optionsRight}
            form={form}
            safetyModelExcess={safetyModelExcess}
            bubbleDirection="top"
            compact={compact}
            delayToShowSavePrompt={delayToShowSavePrompt}
            disableSavePrompt={disableSavePrompt}
            hideLinks={hideLinks}
            backLink={backLink}
            locationState={locationState}
            savedCompareEndPoint={savedCompareEndPoint}
            sharedCompareEndPoint={sharedCompareEndPoint}
            financeOptionText={financeOptionText}
            findDealerUrl={findDealerUrl}
          />
          <Section textAlign={"center"}>
            <div style={styles.header}>
              <div style={{ flex: "0 1 33%" }}>
                <div
                  style={[
                    compact &&
                      styles.vehicleIcon(activeOptions === 0, compact)
                  ]}
                  onClick={() => {
                    this.updateActiveOptions(0)
                  }}>
                  <ConfiguredVehicle
                    src={
                      optionsLeft.grade
                        ? optionsLeft.grade.imageSrc
                        : optionsLeft.grades[0].imageSrc
                    }
                  />
                </div>
              </div>
              <div style={{ flex: compact? "0 0 20%" : "0 0 33%" }}>
                <Arrows />
              </div>
              <div style={{ flex: "0 1 33%" }}>
                <div
                  style={[
                    compact &&
                      styles.vehicleIcon(activeOptions === 1, compact)
                  ]}
                  onClick={() => {
                    this.updateActiveOptions(1)
                  }}>
                  <ConfiguredVehicle
                    src={
                      optionsRight.grade
                        ? optionsRight.grade.imageSrc
                        : optionsRight.grades[0].imageSrc
                    }
                  />
                </div>
              </div>
            </div>
          </Section>
          {compact && (
            <Section textAlign={"center"}>
              <div style={{ position: "relative", width: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center"
                  }}>
                  <div
                    style={[
                      { flex: "0 1 33%" },
                      compact &&
                        styles.vehicleIcon(activeOptions === 0, compact)
                    ]}
                    onClick={() => {
                      this.updateActiveOptions(0)
                    }}>
                    <H3 withDetails>{optionsLeft.nameplate.name}</H3>
                    {
                      activeOptions === 0 &&
                      <div style={styles.arrow} />
                    }
                  </div>
                  <div style={{ flex: "0 0 20%" }} />
                  <div
                    style={[
                      { flex: "0 1 33%" },
                      compact &&
                        styles.vehicleIcon(activeOptions === 1, compact)
                    ]}
                    onClick={() => {
                      this.updateActiveOptions(1)
                    }}>
                    <H3 withDetails>{optionsRight.nameplate.name}</H3>
                    {
                      activeOptions === 1 &&
                      <div style={styles.arrow} />
                    }
                  </div>
                </div>
              </div>
            </Section>
          )}
          <Section horizontalPadding={compact ? false : true}>
            {compact ? (
              <div style={[styles.options(false), { backgroundColor: "#f5f5f5" }]}>
                <div style={{ flex: "0 1 90%" }}>
                  <RenderSelection
                    options={
                      this.state[
                        !activeOptions ? "optionsLeft" : "optionsRight"
                      ]
                    }
                    onChange={
                      !activeOptions
                        ? this.handleChangeOptionLeft
                        : this.handleChangeOptionRight
                    }
                  />
                  {this.renderSafetyModelOption(!activeOptions)}
                </div>
              </div>
            ) : (
              <div style={styles.options(false)}>
                <div data-test="selection-left" style={{ flex: "0 1 33%", width: "33%" }}>
                  <RenderSelection
                    options={optionsLeft}
                    onChange={this.handleChangeOptionLeft}
                    heading
                  />
                  {this.renderSafetyModelOption()}
                </div>
                <div style={{ flex: "0 1 33%", width: "33%" }} />
                <div data-test="selection-right" style={{ flex: "0 1 33%", width: "33%" }}>
                  <RenderSelection
                    options={optionsRight}
                    onChange={this.handleChangeOptionRight}
                    heading
                  />
                  {this.renderSafetyModelOption(false)}
                </div>
              </div>
            )}
            {
              this.state.isModalOpen &&
              <Modal
                width="600px"
                handleClose={() => {
                  this.setState({ isModalOpen: false })
                }}>
                <Content xmlString={safetyModelBlurb} textAlign="left" />
                <Button
                  styleName="black"
                  onClick={() => {
                    this.setState({ isModalOpen: false })
                  }}>
                  Close
                </Button>
              </Modal>
            }
          </Section>

          {/* NON-STICKY PRICE BAR */}

          <div style={styles.pricing}>
            <Section>
              <div
                ref={c => {
                  this.priceBarEl = c
                }}
                style={{ display: "flex", justifyContent: "center", width: '100%' }}>
                <RenderPrice
                  dataTest="price-left"
                  selected={activeOptions === 0}
                  displayPrice={!!optionsLeft.engine}
                  compact={compact}
                  safetyModel={form.safetyModelLeft.value}
                  onClickPriceDisclaimer={() =>
                    this.onClickPriceDisclaimer(
                      "FED014",
                      this.state.optionsLeft
                    )
                  }
                  price={price.data[0]}
                />
                {!compact && <div style={{ flex: "0 0 33%" }} />}
                <RenderPrice
                  dataTest="price-right"
                  selected={activeOptions === 1}
                  displayPrice={!!optionsRight.engine}
                  compact={compact}
                  safetyModel={form.safetyModelRight.value}
                  onClickPriceDisclaimer={() =>
                    this.onClickPriceDisclaimer(
                      "FED015",
                      this.state.optionsRight
                    )
                  }
                  price={price.data[1]}
                />
              </div>
            </Section>
          </div>
        </div>
        <Section horizontalPadding>
          <div style={{width:'100%'}}>
            <H5>Vehicle specifications</H5>
            <FormGroup center show checkbox>
              <InputCheckBox
                label="Only show differences"
                {...fromLinkInputs(this, "showDifferences")} />
            </FormGroup>
            {specifications.data &&
              specifications.data.categories.map((category, index) => {
                let differentItems = []
                if (category.items && category.items.length && form.showDifferences.value) {
                  differentItems = category.items.filter(item => item.contentA !== item.contentB)
                }
                return (
                  <AccordionText
                    styleName='box'
                    isExpandedByDefault={1}
                    titleTextAlign={"center"}
                    title={category.name}
                    key={index}
                    status={1}>
                    {
                      form.showDifferences.value &&
                      optionsLeft.engine &&
                      optionsRight.engine ?
                      (
                        differentItems.length ?
                        differentItems.map((item, i) => (
                          <DuoLine
                            compact={compact}
                            {...item}
                            key={i}
                            showLeft={!!optionsLeft.engine}
                            showRight={!!optionsRight.engine}
                          />
                        )) : (
                          <div style={{ padding: "2em 0", textAlign: "center" }}>
                            There are no differences for your selection.
                          </div>
                        )
                      ) : (
                        category.items &&
                        category.items.length &&
                        category.items.map((item, i) => (
                          <DuoLine
                            compact={compact}
                            {...item}
                            key={i}
                            showLeft={!!optionsLeft.engine}
                            showRight={!!optionsRight.engine}
                          />
                        ))
                      )
                    }
                  </AccordionText>
                )
              })}
          </div>
        </Section>
        <VehicleCTAs
          isLeftCTAsVisible={!!this.state.optionsLeft.engine}
          isRightCTAsVisible={!!this.state.optionsRight.engine}
          findDealerUrl={findDealerUrl}
          vehicleLeft={this.state.optionsLeft}
          vehicleRight={this.state.optionsRight}
          activeOptions={activeOptions}
          compact={compact}
          onClickCTA={this.onClickCTA}
        />
        {components && components.length ? componentParser(components) : null}
      </div>
    )
  }
}

CompareTool.propTypes = {
  vehicles: PropTypes.array,
  initialResults: PropTypes.array,
  findDealerUrl: PropTypes.string,
  hideLinks: PropTypes.bool,
  compact: PropTypes.bool,
  gtmEvent: PropTypes.func,
  priceApiEndpoint: PropTypes.string,
  specsApiEndpoint: PropTypes.string,
  backLink: PropTypes.string,
  heading: PropTypes.string,
  savedCompareEndPoint: PropTypes.string,
  sharedCompareEndPoint: PropTypes.string,
  locationState: PropTypes.object,
  specifications: PropTypes.shape({
    data: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    update: PropTypes.func,
    setOptions: PropTypes.func
  }),
  price: PropTypes.shape({
    data: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
    update: PropTypes.func,
    setOptions: PropTypes.func
  })
}

export default withRadium(
  SearchParams({
    exposeParams: [
      "cars",
      "selectionLeft",
      "selectionRight",
      "bodyStyleLeft",
      "gradeLeft",
      "engineLeft",
      "modelCodeLeft",
      "bodyStyleRight",
      "gradeRight",
      "engineRight",
      "modelCodeRight"
    ]
  }),
  DetectCompact(),
  ConnectLocation(),
  gtmEvent(),
  Price({ expose: "price" }),
  Specs({ expose: "specifications" })
)(CompareTool)

const styles = {
  base: {
    boxSizing: "border-box"
  },
  options: sticky => ({
    display: "flex",
    textAlign: "center",
    alignItems: "center",
    justifyContent: "center",
    padding: sticky ? "1.5em 0" : 0,
    width: '100%'
  }),
  vehicleIcon: (selected, compact) => ({
    opacity: selected ? 1 : compact ? 0.5 : 1,
    cursor: "pointer"
  }),
  header: {
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    width: '100%',
    height: '100%',
    justifyContent: "center"
  },
  pricing: {
    background: "#565c65",
    alignItems: "space-around",
    color: "white"
  },
  safetyModel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid #D5D5D5",
    minHeight: "49px"
  },
  infoIcon: {
    height: "16px",
    width: "16px",
    fill: "#999999",
    paddingLeft: "6px",
    cursor: "pointer",
    verticalAlign: "middle"
  },
  arrow: {
    width: 0,
    height: 0,
    margin: "0 auto",
    borderLeft: "10px solid transparent",
    borderRight: "10px solid transparent",
    borderBottom: "10px solid #f5f5f5"
  }
}



// WEBPACK FOOTER //
// ./src/containers/compare-tool/index.jsx